import React from 'react';
import { useContext } from 'react';
import { AccountContext } from '../../../components/AccountContext';
import { getInvoicesToSettle } from '../../../services/apps/InvoiceCommissionService';
import { FormattedMessage, defineMessages, useIntl } from 'react-intl';
import { useHistory } from 'react-router-dom';
import { Panel } from 'primereact/panel';
import { Field, Form, Formik } from 'formik';
import * as Yup from 'yup';
import gql from 'graphql-tag';
import { InputTextComponent } from '../../../components/prime-formik';
import FileDropzone from '../../../components/file-dropzone/FileDropzone';
import { Button } from 'primereact/button';
import { growlErrors } from '../../../utils/NotificationUtil';
import { NotificationContext } from '../../../components/GrowlNotifications';
import { deleteInvoicesToSettle } from '../../../services/apps/InvoiceCommissionService';
import { useMutation } from '@apollo/react-hooks';

const CREATE_COMMISSION_INVOICE_MUTATION = gql`
  mutation createCommissionInvoiceMutation($input: CreateCommissionInvoiceInput!) {
    createCommissionInvoice(input: $input) {
      account {
        id
      }
    }
  }
`;

const i18nMessages = defineMessages({
  makeAndUploadCommissionInvoice: {
    id: 'account.create-invoice-commission.make-and-upload',
    defaultMessage: 'Make and upload your commission invoice',
  },
  uploadCommissionInvoiceButton: {
    id: 'account.create-invoice-commission.upload-commission-invoice-button',
    defaultMessage: 'Upload commission invoice',
  },
});

const CreateInvoiceCommission = () => {
  const currentAccount = useContext(AccountContext);
  const growl = useContext(NotificationContext);

  const { formatMessage } = useIntl();
  const history = useHistory();

  const invoicesToSettle = getInvoicesToSettle();

  const totalCommissionValue = invoicesToSettle.reduce((accumulator, invoice) => {
    return accumulator + invoice.commissionValue;
  }, 0);

  const [createCommissionInvoiceMutation] = useMutation(CREATE_COMMISSION_INVOICE_MUTATION);

  const createCommissionInvoice = async ({ commissionInvoiceNumber, file }) => {
    try {
      await createCommissionInvoiceMutation({
        variables: {
          input: {
            commissionInvoiceNumber,
            accountId: currentAccount,
            fieldName: '1',
            invoicesToSettle: invoicesToSettle.map(
              ({
                idOfPayingAccount,
                nameOfPayingAccount,
                typeOfPayingAccount,
                invoiceId,
                invoiceNumber,
                initialInvoiceValue,
                commissionValue,
                gpCommissionValue,
                gpAccountName,
              }) => {
                return {
                  accountId: idOfPayingAccount,
                  accountName: nameOfPayingAccount,
                  accountType: typeOfPayingAccount,
                  stripeInvoiceId: invoiceId,
                  stripeInvoiceNumber: invoiceNumber,
                  initialInvoiceValue,
                  commission: commissionValue,
                  gpCommissionValue,
                  gpAccountName,
                };
              },
            ),
          },
          file,
        },
      });

      deleteInvoicesToSettle();
      history.push(`/accounts/${currentAccount}/settled-commission?invoiceCommissionCreated=true`);
    } catch (mutationError) {
      growlErrors(growl, mutationError);
    }
  };

  const initialValues = {
    commissionInvoiceNumber: null,
    file: null,
  };

  const validationSchema = Yup.object().shape({
    commissionInvoiceNumber: Yup.string().required(),
    file: Yup.mixed().required(),
  });

  return (
    <div className="p-grid">
      <div className="p-col-12">
        <div className="brand-detailed-wrapper">
          <div className="p-sm-12 p-md-6">
            <Panel header={formatMessage(i18nMessages.makeAndUploadCommissionInvoice)}>
              <Formik
                enableReinitialize
                initialValues={initialValues}
                validationSchema={validationSchema}
                onSubmit={(values) => createCommissionInvoice(values)}
              >
                {({ isValid, isSubmitting, handleSubmit }) => {
                  return (
                    <Form onSubmit={handleSubmit}>
                      <div className="p-col-12">
                        <FormattedMessage
                          id="account.create-invoice-commission.total-commission-value"
                          // eslint-disable-next-line
                          defaultMessage="You can invoice ${totalCommissionValue} excl. of VAT and other applicable duties:"
                          tagName="p"
                          values={{
                            totalCommissionValue: totalCommissionValue.toFixed(2),
                          }}
                        />
                        <FormattedMessage
                          id="account.create-invoice-commission.make-commission-invoice"
                          defaultMessage="1. Make the commission invoice now"
                          tagName="p"
                        />

                        <FormattedMessage
                          id="account.create-invoice-commission.enter-commission-invoice-number"
                          defaultMessage="2. Enter the commission invoice number:"
                          tagName="p"
                        />
                        <Field name="commissionInvoiceNumber" component={InputTextComponent} />

                        <FormattedMessage
                          id="account.create-invoice-commission.upload-commission-invoice"
                          defaultMessage="3. Upload the commission invoice"
                          tagName="p"
                        />
                        <FileDropzone />

                        <br />
                        <div className="align-center">
                          <Button
                            type="submit"
                            disabled={!isValid || isSubmitting}
                            label={formatMessage(i18nMessages.uploadCommissionInvoiceButton)}
                          />
                        </div>
                      </div>
                    </Form>
                  );
                }}
              </Formik>
            </Panel>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateInvoiceCommission;
